/* eslint-disable react-refresh/only-export-components */
import type { LoggerCore, LoggerOptions } from "@/services/logger/logger-core";
import { createLogger } from "@/services/logger/logger-core";
import { createContext, useCallback, useEffect, useMemo, useRef } from "react";
import { loggerStorage } from "./logger-storage";
import { getOrCreateInstanceId } from "../instance-id";
import { getItem, removeItem, setItem } from "@/lib/storage-utils";
import { STORAGE } from "@/constants";
import { sendLogs } from "./logs-service";

const ABRUPT_EXIT_LOGS_KEY = "loggerAbruptExitPending";

type LoggerContextValue = {
  logger: LoggerCore;
};

type LoggerProviderProps = {
  children: React.ReactNode;
  options?: Omit<LoggerOptions, "sendLogs">;
};

export const LoggerContext = createContext<LoggerContextValue | null>(null);

export function LoggerProvider({ children, options }: LoggerProviderProps) {
  const loggerRef = useRef<LoggerCore | null>(null);

  if (!loggerRef.current) {
    const sendLogsWithFallback = async (
      logs: LoggerCore["drainBuffer"] extends () => infer T ? T : never,
    ) => {
      try {
        // Normal runtime sends rely on interceptor's current instanceId.
        await sendLogs(logs);
      } catch (error) {
        await loggerStorage.saveLogs(logs);
        throw error;
      }
    };
    loggerRef.current = createLogger({
      ...options,
      sendLogs: sendLogsWithFallback,
    });
  }

  const replayPersistedBatches = useCallback(
    async (respectAbruptFlag: boolean) => {
      const shouldReplayAbruptBatches = Boolean(
        getItem<boolean>(ABRUPT_EXIT_LOGS_KEY, STORAGE.LOCAL),
      );
      if (respectAbruptFlag && !shouldReplayAbruptBatches) return;

      try {
        const batches = await loggerStorage.loadAllLogs();
        if (batches.length === 0) {
          if (shouldReplayAbruptBatches) {
            removeItem(ABRUPT_EXIT_LOGS_KEY, STORAGE.LOCAL);
          }
          return;
        }

        let allSent = true;
        for (const batch of batches) {
          try {
            await sendLogs(
              batch.events,
              batch.instanceId && batch.instanceId !== "unknown"
                ? batch.instanceId
                : undefined,
            );
            await loggerStorage.deleteBatch(batch.id);
          } catch {
            allSent = false;
          }
        }

        if (allSent) {
          removeItem(ABRUPT_EXIT_LOGS_KEY, STORAGE.LOCAL);
        }
      } catch {
        // Ignore storage load failures and continue runtime logging.
      }
    },
    [],
  );

  useEffect(() => {
    const logger = loggerRef.current;

    return () => {
      logger?.destroy();
    };
  }, []);

  useEffect(() => {
    void replayPersistedBatches(true);
  }, [replayPersistedBatches]);

  // On tab close or hide: persist buffered logs immediately to IndexedDB.
  // We cannot rely on logger.flush() here because it is async (API call); the browser
  // often terminates the page before that completes, so saveLogs in the catch never runs.
  // Draining the buffer and calling saveLogs() starts the IDB write right away so it can complete.
  useEffect(() => {
    const logger = loggerRef.current;
    if (!logger) return;

    const persistBuffer = () => {
      const pending = logger.drainBuffer();
      if (pending.length === 0) return;
      const instanceId = getOrCreateInstanceId();
      loggerStorage.saveLogs(pending, instanceId);
      setItem(ABRUPT_EXIT_LOGS_KEY, true, STORAGE.LOCAL);
    };

    // pagehide/beforeunload represent real page lifecycle exit paths.
    window.addEventListener("pagehide", persistBuffer);
    window.addEventListener("beforeunload", persistBuffer);

    return () => {
      window.removeEventListener("pagehide", persistBuffer);
      window.removeEventListener("beforeunload", persistBuffer);
    };
  }, []);

  // Extra safety trigger: on any form submit, flush in-memory buffer
  // and replay persisted batches so IndexedDB data is minimized.
  useEffect(() => {
    const logger = loggerRef.current;
    if (!logger) return;

    const onSubmitCapture = () => {
      logger.flush();
      void replayPersistedBatches(false);
    };

    document.addEventListener("submit", onSubmitCapture, true);
    return () => {
      document.removeEventListener("submit", onSubmitCapture, true);
    };
  }, [replayPersistedBatches]);

  const contextValue = useMemo(() => {
    return loggerRef.current ? { logger: loggerRef.current } : null;
  }, []);

  return (
    <LoggerContext.Provider value={contextValue}>
      {children}
    </LoggerContext.Provider>
  );
}
